import React, { useState, useMemo, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useUserData } from '../../../hooks/useUserData';
import { buildApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemo } from '../../../contexts/DemoContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { getChartTruncationConfig } from '../../../utils/chartUtils';
import ChartUpgradePrompt from '../../../components/charts/ChartUpgradePrompt';

const AssetAllocationChart = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewType, setViewType] = useState('bar');
  const { userData } = useUserData();
  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams, isDemoModeActive } = useDemo();
  const { canProjectBeyond5Years } = useSubscription();

  // Asset type colors
  const assetColors = {
    'Equity': '#1E40AF',
    'Mutual Funds': '#059669',
    'Real Estate': '#F59E0B',
    'PPF': '#EF4444',
    'FDs': '#64748B',
    'Gold': '#DC2626',
    'Bonds': '#8B5CF6',
    'Cash': '#10B981',
    'Insurance': '#F97316',
    'Other': '#6B7280'
  };

  // Generate year options from Setup startYear to projection horizon
  // Apply 5-year limit for free users
  const yearOptions = useMemo(() => {
    const startYear = Number(userData?.setup?.startYear) || new Date().getFullYear();
    const horizon = Math.max(1, Math.min(60, Number(userData?.setup?.projectionHorizon) || 20));
    const truncationConfig = getChartTruncationConfig('AssetAllocationDashboard', canProjectBeyond5Years, isDemoModeActive);
    const effectiveHorizon = truncationConfig.shouldTruncate ? Math.min(horizon, 5) : horizon;
    return Array.from({ length: effectiveHorizon + 1 }, (_, i) => startYear + i);
  }, [userData?.setup?.startYear, userData?.setup?.projectionHorizon, canProjectBeyond5Years]);

  // Ensure selected year defaults to start year
  useEffect(() => {
    if (yearOptions && yearOptions.length > 0) {
      setSelectedYear(prev => (yearOptions.includes(prev) ? prev : yearOptions[0]));
    }
  }, [yearOptions]);

  const [assetData, setAssetData] = useState({});
  useEffect(() => {
    const fetchYear = async (year) => {
      try {
        const params = new URLSearchParams({ year: String(year) });
        const demoParams = getDemoQueryParams();
        const separator = params.toString() ? '&' : '';
        const fullParams = `${params.toString()}${demoParams ? separator + demoParams.substring(1) : ''}`;
        const res = await fetch(buildApiUrl(`/api/dashboard/asset-allocation?${fullParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load allocation');
        const json = await res.json();
        if (json?.success) setAssetData(prev => ({ ...prev, [year]: json.data || [] }));
        else setAssetData(prev => ({ ...prev, [year]: [] }));
      } catch (e) {
        console.error(e);
        setAssetData(prev => ({ ...prev, [year]: [] }));
      }
    };
    if (yearOptions && yearOptions.length) fetchYear(selectedYear);
  }, [selectedYear, userData, getDemoQueryParams, getAuthHeaders]);

  const formatCurrency = (value) => {
    if (value >= 10000000) { // 1 Cr = 10,000,000
      return `₹${(value / 10000000).toFixed(1)} Cr`;
    }
    if (value >= 100000) { // 1 Lakh = 100,000
      return `₹${(value / 100000).toFixed(1)} L`;
    }
    if (value >= 1000) {
      return `₹${(value / 1000).toFixed(0)} K`;
    }
    return `₹${Math.round(value).toLocaleString('en-IN')}`;
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
          <p className="font-medium text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(data.value)} ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const currentData = assetData[selectedYear] || [];
  const totalValue = currentData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Asset Allocation</h3>
          <p className="text-sm text-muted-foreground">Portfolio distribution over time</p>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-1">
            <Button
              variant={viewType === 'pie' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewType('pie')}
              className="px-3 py-1"
            >
              <Icon name="PieChart" size={16} />
            </Button>
            <Button
              variant={viewType === 'bar' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewType('bar')}
              className="px-3 py-1"
            >
              <Icon name="BarChart3" size={16} />
            </Button>
          </div>
          
          {/* Export button - temporarily disabled
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} />
            Export
          </Button>
          */}
        </div>
      </div>

      {/* Year Selector */}
      <div className="flex items-center gap-2 mb-6">
        <span className="text-sm font-medium text-foreground">Year:</span>
        <select
          className="text-sm border border-border rounded px-2 py-1 bg-background"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {yearOptions.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <div className="ml-auto text-sm text-muted-foreground">
          Total: <span className="font-medium text-foreground">{formatCurrency(totalValue)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chart */}
        <div className="lg:col-span-3">
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              {viewType === 'pie' ? (
                <PieChart>
                  <Pie
                    data={currentData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {currentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              ) : (
                <BarChart data={currentData} margin={{ top: 20, right: 60, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="name" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={11}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={({ x, y, payload }) => {
                      const text = payload.value;
                      // Less aggressive truncation - allow longer text
                      const displayText = text.length > 12 ? text.substring(0, 12) + '...' : text;
                      return (
                        <text x={x} y={y} dy={16} textAnchor="start" fill="var(--color-muted-foreground)" fontSize={11}>
                          {displayText}
                        </text>
                      );
                    }}
                  />
                  <YAxis 
                    tickFormatter={formatCurrency}
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {currentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Legend & Details */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-foreground">Asset Breakdown</h4>
          <div className="space-y-3">
            {currentData.map((asset, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: asset.color }}
                  ></div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{asset.name}</p>
                    <p className="text-xs text-muted-foreground">{asset.percentage}%</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">{formatCurrency(asset.value)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-border">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Liquid Assets:</span>
              <span className="font-medium text-foreground">
                {formatCurrency(currentData.slice(0, 2).reduce((sum, item) => sum + item.value, 0))}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm mt-1">
              <span className="text-muted-foreground">Illiquid Assets:</span>
              <span className="font-medium text-foreground">
                {formatCurrency(currentData.slice(2).reduce((sum, item) => sum + item.value, 0))}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Show upgrade prompt if years are limited */}
      {(() => {
        const startYear = Number(userData?.setup?.startYear) || new Date().getFullYear();
        const horizon = Math.max(1, Math.min(60, Number(userData?.setup?.projectionHorizon) || 20));
        const truncationConfig = getChartTruncationConfig('AssetAllocationDashboard', canProjectBeyond5Years, isDemoModeActive);
        const effectiveHorizon = truncationConfig.shouldTruncate ? Math.min(horizon, 5) : horizon;
        const isTruncated = horizon > effectiveHorizon;

        return isTruncated && truncationConfig.showUpgradePrompt ? (
          <ChartUpgradePrompt
            visibleYears={effectiveHorizon}
            totalYears={horizon}
            truncatedAtYear={startYear + effectiveHorizon}
            compact={true}
          />
        ) : null;
      })()}
    </div>
  );
};

export default AssetAllocationChart;